import en from "../core/locales/en.json";
import hi from "../core/locales/hi.json";
import { useEffect, useSyncExternalStore } from "react";
import Api from "../core/data/redux/utils/apiCall";
import { BASE_URLS } from "../core/data/redux/apis/urls";

const STORAGE_KEY = "app_language";
const LANG_VERSION_KEY = "langVersion";

type Lang = "en" | "hi" | string;

// Load from local storage initially if available
let cachedTranslations: Record<string, any> = {};
try {
  const cachedHi = localStorage.getItem("translations_hi");
  if (cachedHi) {
    cachedTranslations.hi = JSON.parse(cachedHi);
  }
} catch (e) {
  // ignore
}

const locales: Record<Lang, Record<string, string>> = {
  en: en as Record<string, string>,
  hi: { ...(hi as Record<string, string>), ...(cachedTranslations.hi || {}) },
};

let current: Lang = (localStorage.getItem(STORAGE_KEY) as Lang) || "en";

const listeners = new Set<() => void>();
const notifyListeners = () => listeners.forEach((l) => l());

export const initTranslations = async () => {
  try {
    const payload = { lang: current };
    const versionRes = await Api.post(`${BASE_URLS.BASE_URL}languages/check-version`, payload);
    const responseData = versionRes?.data || versionRes; // Handle intercepted format

    if (responseData && responseData.current_version !== undefined) {
      const localVersion = parseInt(localStorage.getItem(LANG_VERSION_KEY) || "0", 10);
      
      if (localVersion < responseData.current_version || Object.keys(locales[current] || {}).length === 0) {
        const transRes = await Api.post(`${BASE_URLS.BASE_URL}languages/get-trans`, payload);
        const transData = transRes?.data || transRes;
        
        if (transData && transData.translation) {
          locales[current] = transData.translation;
          localStorage.setItem(`translations_${current}`, JSON.stringify(transData.translation));
          localStorage.setItem(LANG_VERSION_KEY, responseData.current_version.toString());
          notifyListeners();
        }
      }
    }
  } catch (error) {
    console.error("Failed to fetch dynamic translations:", error);
  }
};

export const getLanguage = (): Lang => current;

export const setLanguage = async (lang: Lang) => {
  if (current === lang) return;
  current = lang;
  try {
    localStorage.setItem(STORAGE_KEY, lang);
  } catch (e) {
    // ignore
  }
  notifyListeners();
  
  if (lang !== "en") {
     await initTranslations();
  }
};

export const t = (key: string): string => {
  if (!key) return key;
  const val = locales[current]?.[key];
  if (val) return val;
  return locales.en[key] || key;
};

export const translateLabel = (label: string): string => {
  if (!label) return label;
  if (/^[a-z0-9_]+$/.test(label)) {
    return t(label);
  }

  const englishEntries = Object.entries(locales.en);
  const normalizedLabel = label.toLowerCase();

  const found = englishEntries.find(([, value]) => value.toLowerCase() === normalizedLabel);
  if (found) {
    const key = found[0];
    return locales[current]?.[key] || found[1];
  }

  const partial = englishEntries.find(([, value]) => {
    if (!value) {
      return false;
    }
    const normalizedValue = value.toLowerCase();
    if (
      !normalizedLabel.startsWith(normalizedValue) ||
      normalizedLabel.length === normalizedValue.length
    ) {
      return false;
    }
    const boundaryChar = normalizedLabel.charAt(normalizedValue.length);
    return /[\s(:\-–—]/.test(boundaryChar);
  });

  if (partial) {
    const [key, value] = partial;
    const translatedPrefix = locales[current]?.[key] || value;
    return `${translatedPrefix}${label.slice(value.length)}`;
  }

  return label;
};

export const tBoth = (key: string): string => {
  if (!key) return key;
  const enVal = locales.en[key] || key;
  const hiVal = locales.hi?.[key] || key;
  if (enVal === hiVal) return enVal;
  return `${enVal} / ${hiVal}`;
};

// useT hook — returns a bound t() and re-renders when language changes
export const useT = () => {
  const snapshot = () => current;
  const subscribe = (onStoreChange: () => void) => {
    listeners.add(onStoreChange);
    return () => listeners.delete(onStoreChange);
  };
  const lang = useSyncExternalStore(subscribe, snapshot);

  useEffect(() => {
    // side-effect: update document language attribute
    try {
      document.documentElement.setAttribute("lang", lang);
    } catch (e) {
      // ignore
    }
  }, [lang]);

  return { t, lang, setLanguage, getLanguage, tBoth } as const;
};

export default { getLanguage, setLanguage, t, translateLabel, useT, tBoth, initTranslations };
